import { useState, useEffect } from 'react';
import { Check, Trophy } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import Modal from './Modal';
import { safeFormatDate, formatDistance } from '../../lib/utils';
import { useWeeklyStats } from '../../hooks/useProfile';

interface WorkoutCompletionModalProps {
  workouts: any[];
  onClose: () => void;
  show: boolean;
}

export default function WorkoutCompletionModal({
  workouts,
  onClose,
  show,
}: WorkoutCompletionModalProps) {
  const { getAccessTokenSilently } = useAuth0();
  const { isLoading, weeklyStats } = useWeeklyStats();
  const [animationStep, setAnimationStep] = useState(0);
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    if (show && workouts.length > 0) {
      // Start animation sequence
      const timer = setTimeout(() => {
        setAnimationStep(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [show, workouts.length]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const handleAcknowledge = async () => {
    if (acknowledging) return;

    setAcknowledging(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/workouts/acknowledge`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            workoutIds: workouts.map((w) => w.id),
          }),
        },
      );

      if (response.ok) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to acknowledge workouts:', error);
    } finally {
      setAcknowledging(false);
    }
  };

  if (!show || workouts.length === 0 || isLoading) return null;

  return (
    <Modal show={show} onClose={onClose} title="🎉 Great Work!">
      <div className="space-y-6 text-center">
        <div className="mb-4 text-gray-700 dark:text-gray-300">
          You've completed {workouts.length} workout
          {workouts.length > 1 ? 's' : ''} since your last visit!
        </div>

        {/* Workout Cards */}
        <div className="space-y-3 max-h-60 overflow-y-auto">
          {workouts.map((workout, index) => (
            <div
              key={workout.id}
              className={`transform rounded-lg border border-brand-green/30 bg-gradient-to-r from-green-50 to-blue-50 p-4 transition-all duration-500 dark:border-brand-volt/40 dark:from-[#14243a] dark:to-[#10253a] ${
                animationStep >= 1
                  ? 'scale-100 opacity-100'
                  : 'scale-95 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-greenSoft dark:bg-brand-volt">
                    <Check className="h-6 w-6 text-brand-midnight" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {workout.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {safeFormatDate(
                        workout.completedAt,
                        'Recently completed',
                      )}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {workout.distance > 0 ? (
                    <div className="text-lg font-bold text-brand-cobalt">
                      {formatDistance(workout.distance)}
                    </div>
                  ) : null}
                  {workout.elapsedTime > 0 ? (
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {formatTime(workout.elapsedTime)}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Weekly Stats with Animation */}
        <div
          className={`transform rounded-lg border border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 p-6 transition-all duration-700 dark:border-purple-400/40 dark:from-[#211838] dark:to-[#17213d] ${
            animationStep >= 1 ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
          style={{ transitionDelay: '800ms' }}
        >
          <h3 className="mb-4 flex items-center justify-center space-x-2 font-semibold text-gray-900 dark:text-white">
            <Trophy className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <span>This Week's Progress</span>
          </h3>

          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 mb-1">
                {weeklyStats.workoutCount}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Workouts
              </div>
            </div>

            {weeklyStats.totalDistance > 0 ? (
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-cobalt mb-1">
                  {formatDistance(weeklyStats.totalDistance)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  Distance
                </div>
              </div>
            ) : null}

            {weeklyStats.totalTime > 0 ? (
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-green mb-1">
                  {formatTime(weeklyStats.totalTime)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  Time
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Action Button */}
        <div className="pt-4">
          <button
            onClick={handleAcknowledge}
            disabled={acknowledging}
            className={`w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold transform transition-all duration-200 ${
              acknowledging
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:from-blue-600 hover:to-purple-700 hover:scale-105'
            }`}
          >
            {acknowledging ? 'Processing...' : 'Awesome! Continue Training'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
